/* eslint-disable react/react-in-jsx-scope */
/*
 * File: ProductSelectionCard.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 20 Dec 2021 11:59:23 am
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Card, Col, Divider, message, Row, Space, Tooltip, Typography } from 'antd';
import { MessageType } from 'antd/lib/message';
import { useField } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { ProductSubType } from 'models/ProductSubType';
import { ReleasedProductModel } from 'models/ReleasedProductModel';
import { SalesOrderLineItemModel } from 'models/SalesOrderLineItemModel';
import { useState } from 'react';
import { AddButton } from './AddButton';
import { QuantityInput } from './QuantityInput';

interface ProductSelectionCardProps {
  product: ReleasedProductModel;
  record?: SalesOrderLineItemModel;
  index?: number;
}

const styles: InlineStylesModel = {
  cardBody: {
    borderRadius: 10,
    padding: 0,
    boxShadow: '0 0 4px rgb(0 0 0 / 10%)'
  },
  customerInfo: {
    marginTop: 10
  },
  title: {
    fontSize: '.8rem',
    margin: 0,
    color: 'rgba(0, 0, 0, .4)'
  },
  manufacturerText: {
    fontSize: '.8rem',
    margin: 0,
    color: 'rgba(0, 0, 0, .4)'
  },
  addButtonContainer: {
    position: 'absolute',
    right: 40,
    top: '50%',
    transform: 'translateY(-50%)',
    transition: '.5s width ease-out',
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    height: 30,
    width: 30,
    color: 'white',
    cursor: 'pointer',
    boxShadow: '0 0 4px rgba(0,0,0,.1)',
    zIndex: 1111
  },
  quantityTitle: {
    fontSize: '.8rem',
    marginBottom: 7,
    color: 'rgba(0, 0, 0, .4)'
  },
  titleWhite: {
    fontSize: '.8rem',
    margin: 0,
    color: 'rgba(255, 255, 255, .4)'
  },
  values: {
    fontSize: 16,
    fontWeight: 600
  },
  valuesWhite: {
    color: 'rgba(255, 255, 255, .9)',
    fontSize: 16,
    fontWeight: 600
  },
  minusButton: {
    color: '#F93943',
    borderColor: '#F93943'
  },
  selectedStyles: {
    transition: '.25s all ease-out',
    background: '#4e937aab',
    boxShadow: '0 0 6px rgba(0,0,0,.4)',
    borderRadius: 10
  },
  icon: {
    position: 'absolute',
    top: 10,
    right: 10,
    fontWeight: 'bolder',
    fontSize: 30,
    borderRadius: '50%',
    transition: '1s all ease-out'
  },
  triangle: {
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '183px',
    height: '101px',
    borderLeft: 'solid 50.5px rgba(0,0,0,.2)',
    borderBottom: 'solid 50.5px transparent',
    borderTop: 'solid 50.5px transparent'
  }
};

export const SublineProductSelectionCard = ({ product, record, index }: ProductSelectionCardProps): JSX.Element => {
  const [quantity, _setQuantity] = useState('');

  const [x, { value }, { setValue }] = useField<SalesOrderLineItemModel>(`lines.${index}`);


  const isDuplicate = record?.subLines?.some((child) => product.productNumber === child.productNumber);

  const handleAddProduct = (): void | MessageType => {
    const lineItemToAdd: ProductSubType = {
      productNumber: product.productNumber ?? '',
      productDescription: product.productDescription ?? '',
      quantity: Number(quantity) ?? undefined,
      lineNumber: value.lineNumber + (((value?.subLines?.length ?? 0 ) + 1)* 0.01)
    };

    if (!lineItemToAdd.quantity) {
      message.error('You must enter a quantity');

      return;
    }

    setValue({ ...x.value, subLines: x.value.subLines ? [...x.value.subLines, lineItemToAdd] : [lineItemToAdd] });

    message.success('Submodule successfully added');

    _setQuantity('');
  };

  const handleDelete = (): void | MessageType => {
    // const newData = products.map((parentProduct) => {
    //   if (parentProduct.productNumber === record?.productNumber) {
    //     return {
    //       ...parentProduct,
    //       children: parentProduct?.children?.filter((child) => child.productNumber !== product.productNumber)
    //     };
    //   }

    //   return parentProduct;
    // });

    // dispatch(setProducts(newData));
    message.success('Submodule successfully removed');
  };
  const animate = (): void => {
    if (!isDuplicate) setTimeout(() => handleAddProduct(), 500);
    else setTimeout(() => handleDelete(), 500);
  };

  const handleQuantityChange = (value: number | string | null): void => {
    if (value !== null) {
      _setQuantity(String(value));
    } else {
      _setQuantity('');
    }
  };

  return (
    <Card bordered={false} style={styles.cardBody} bodyStyle={styles.cardBody}>
      <Row style={{ borderRadius: 10, height: '100%' }}>
        <Col span={10} style={{ background: 'rgba(0,0,0,.2)', padding: '10px 20px', borderRadius: '10px 0 0 10px' }}>
          <Typography.Paragraph style={styles.title}>Product Number</Typography.Paragraph>
          <Typography.Paragraph style={styles.values} ellipsis>
            <Tooltip
              title={
                <>
                  <Typography.Text style={{ color: 'white' }}>Product Name: {product.productName ?? product.searchName}</Typography.Text>
                  <Typography.Text style={{ color: 'white' }}>Product Description: {product.productDescription}</Typography.Text>
                </>
              }>
              <Typography.Text style={{ fontWeight: 400 }}>{product.productNumber}</Typography.Text>
            </Tooltip>
          </Typography.Paragraph>
          <Typography.Paragraph style={styles.manufacturerText} ellipsis>
            <Tooltip title={`Manufacturer: ${product.manufacturerId}`}>{product.manufacturerId || 'N/A'}</Tooltip>
            <Divider type="vertical" />
            <Tooltip title={`Category: ${product.productGroupId}`}>{product.productGroupId}</Tooltip>
          </Typography.Paragraph>
        </Col>
        {
          <Space onClick={animate} style={styles.addButtonContainer}>
            <AddButton />
          </Space>
        }
        <Col span={14}>
          <div style={styles.triangle} />
          <Row justify="center">
            <Col style={styles.customerInfo} span={14}>
              <Row justify="space-around" align="middle">
                {!isDuplicate ? (
                  <Col span={12}>
                    {/* <Typography.Paragraph style={styles.quantityTitle}>Quantity</Typography.Paragraph> */}
                    {<QuantityInput placeHolder="Quantity" onInputChange={handleQuantityChange} value={String(quantity)} />}
                  </Col>
                ) : (
                  <Col span={16}>
                    <Typography.Paragraph>Product already added, edit in the table or remove here</Typography.Paragraph>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
};
